import { Box, Paper, Typography } from '@mui/material';
import { colors } from '@src/styles';
import { useRunsContext } from '../context/RunsContext';
import { KpiRow, KpiTile } from './KpiTiles';
import { DailyExecutionsChart } from './DailyExecutionsChart';
import { Leaderboard } from './Leaderboard';
import { LogsTable } from './LogsTable';
import { AutomationSearch } from './AutomationSearch';
import { RunsSkeleton } from './RunsSkeleton';
import { ContextSearchField } from './ContextSearchField';
import { pct, fmtDuration, card, cardTitleSX, getLogsEmptyMessage } from '../utils/format';

export function OrgView() {
  const {
    aggregate,
    runs,
    error,
    isLoading,
    logsError,
    isLoadingRuns,
    logsPage,
    logsSort,
    pageSize,
    setLogsPage,
    setLogsSort,
    setSelectedRunId,
    setAutomation,
    contextSearch,
  } = useRunsContext();
  const g = aggregate?.grandTotal;
  const dailyMap = new Map<string, { total: number; failed: number }>();
  (aggregate?.items ?? []).forEach((it) =>
    it.errorRateByDay.forEach((d) => {
      const cur = dailyMap.get(d.date) ?? { total: 0, failed: 0 };
      dailyMap.set(d.date, { total: cur.total + d.total, failed: cur.failed + d.failed });
    }),
  );
  const daily = [...dailyMap.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, v]) => ({ date, succeeded: v.total - v.failed, failed: v.failed }));
  const worstP95 = Math.max(0, ...(aggregate?.items ?? []).map((it) => it.durationMs.p95));

  if (error)
    return (
      <Paper elevation={0} sx={card}>
        <Typography sx={{ color: colors.status.rejected, fontSize: '13.5px' }}>{error}</Typography>
      </Paper>
    );

  if (isLoading && !aggregate) return <RunsSkeleton kpiCount={4} side="leaderboard" withLogs />;

  return (
    <Box
      aria-busy={isLoading}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        opacity: isLoading ? 0.55 : 1,
        transition: 'opacity 0.2s ease',
        pointerEvents: isLoading ? 'none' : 'auto',
      }}
    >
      <KpiRow>
        <KpiTile label="Execuções" value={(g?.total ?? 0).toLocaleString('pt-BR')} />
        <KpiTile
          label="Taxa de sucesso"
          value={pct(g?.succeeded ?? 0, g?.total ?? 0)}
          tone="ok"
          sub={`${g?.failed ?? 0} falhas · ${g?.running ?? 0} em exec.`}
        />
        <KpiTile label="Falhas" value={String(g?.failed ?? 0)} tone="bad" />
        <KpiTile label="Latência p95 (pior automação)" value={fmtDuration(worstP95)} tone="warn" />
      </KpiRow>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1.35fr 1fr',
          gap: 2,
          '@media (max-width:1050px)': { gridTemplateColumns: '1fr' },
        }}
      >
        <Paper elevation={0} sx={{ ...card, minWidth: 0 }}>
          <DailyExecutionsChart data={daily} />
        </Paper>
        <Paper elevation={0} sx={{ ...card, minWidth: 0 }}>
          <Leaderboard
            title="Automações da org"
            rows={(aggregate?.items ?? []).map((it) => ({
              key: it.key,
              label: it.key,
              total: it.total,
              failed: it.failed,
            }))}
            onSelect={(key) => {
              const it = aggregate?.items.find((x) => x.key === key);
              if (it) setAutomation(it.key, it.type);
            }}
          />
        </Paper>
      </Box>
      <Paper elevation={0} sx={card}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            mb: 1.5,
            gap: 2,
          }}
        >
          <Typography component="h2" sx={cardTitleSX}>
            Execuções (logs)
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.25,
              flex: 1,
              minWidth: { xs: '100%', sm: 360 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 220 } }}>
              <ContextSearchField />
            </Box>
            <AutomationSearch items={aggregate?.items ?? []} onSelect={(key, type) => setAutomation(key, type)} />
          </Box>
        </Box>
        {logsError ? (
          <Typography sx={{ color: colors.status.rejected, fontSize: '13.5px' }}>{logsError}</Typography>
        ) : (
          <LogsTable
            items={runs?.items ?? []}
            total={runs?.total ?? 0}
            page={logsPage}
            pageSize={pageSize}
            sortBy={logsSort.by}
            sortDir={logsSort.dir}
            isLoading={isLoadingRuns}
            emptyMessage={getLogsEmptyMessage(contextSearch)}
            onPageChange={setLogsPage}
            onSortChange={setLogsSort}
            onSelect={setSelectedRunId}
          />
        )}
      </Paper>
    </Box>
  );
}
